import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../apiConfig';
import './Accounts.css';

/* ── Avatar helpers ──────────────────────────────────────────────── */
const PALETTE = [
  '#DB4437', '#E91E63', '#9C27B0', '#3F51B5',
  '#1976D2', '#00897B', '#E64A19', '#546E7A',
];

function avatarColor(str) {
  if (!str) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return PALETTE[Math.abs(h) % PALETTE.length];
}

/* ── Icons ───────────────────────────────────────────────────────── */
const GmailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
    <path d="M6 40h36V20L24 32 6 20v20z" fill="#EA4335" opacity=".9" />
    <path d="M6 20l18 12 18-12V10H6v10z" fill="#EA4335" />
    <path d="M6 10v10l18 12V22L6 10z" fill="#C5221F" opacity=".6" />
    <path d="M42 10v10L24 32V22L42 10z" fill="#C5221F" opacity=".4" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

/* ── Step label map ──────────────────────────────────────────────── */
const STEP_LABELS = {
  categorize:          'Categorize',
  requiresResponse:    'Reply check',
  draftResponse:       'Draft',
  evaluateSufficiency: 'Evaluate',
  extractInsights:     'Insights',
};

/* ── ProcessingFeed ──────────────────────────────────────────────── */
function ProcessingFeed({ feed }) {
  // Group events by emailId
  const emails = {};
  let skippedCount = 0;
  let doneEvent = null;

  for (const event of feed) {
    if (event.type === 'skip') {
      skippedCount++;
      continue;
    }
    if (event.type === 'done') {
      doneEvent = event;
      continue;
    }
    if (event.type === 'ping' || event.type === 'error') continue;

    if (!emails[event.emailId]) {
      emails[event.emailId] = { from: '', subject: '', steps: {}, done: false };
    }

    if (event.type === 'email_start') {
      emails[event.emailId].from = event.from;
      emails[event.emailId].subject = event.subject;
    } else if (event.type === 'step') {
      emails[event.emailId].steps[event.step] = event.status;
    } else if (event.type === 'email_done') {
      emails[event.emailId].done = true;
    }
  }

  const emailEntries = Object.entries(emails);

  return (
    <div className="processing-feed">
      {skippedCount > 0 && (
        <div className="processing-feed__skips">
          {skippedCount} email{skippedCount !== 1 ? 's' : ''} already synced — skipped
        </div>
      )}

      {emailEntries.map(([emailId, info]) => {
        const stepEntries = Object.entries(info.steps);
        return (
          <div key={emailId} className={`processing-email-row${info.done ? ' processing-email-row--done' : ''}`}>
            <div className="processing-email-row__header">
              <span className="processing-email-row__sender">{info.from || emailId}</span>
              <span className="processing-email-row__subject">{info.subject}</span>
            </div>
            {stepEntries.length > 0 && (
              <div className="processing-email-row__steps">
                {stepEntries.map(([step, status]) => (
                  <span
                    key={step}
                    className={`step-pill step-pill--${status}`}
                    title={`${STEP_LABELS[step] ?? step}: ${status}`}
                  >
                    {status === 'running' && <span className="step-spinner" />}
                    {status === 'done' && <span className="step-check">✓</span>}
                    {STEP_LABELS[step] ?? step}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {doneEvent && (
        <div className="processing-feed__summary">
          Done — {doneEvent.count} processed
          {doneEvent.skipped > 0 ? `, ${doneEvent.skipped} skipped` : ''}
        </div>
      )}
    </div>
  );
}

/* ── Component ───────────────────────────────────────────────────── */
const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [syncing, setSyncing]   = useState({});
  const [syncResult, setSyncResult] = useState({});
  const [processingFeed, setProcessingFeed] = useState({});

  useEffect(() => {
    fetch(`${API_BASE_URL}/accounts`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => setAccounts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const handleDelete = id => {
    fetch(`${API_BASE_URL}/accounts/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    }).then(() => setAccounts(prev => prev.filter(a => a.id !== id)));
  };

  const handleSync = (id) => {
    setSyncing(prev => ({ ...prev, [id]: true }));
    setSyncResult(prev => ({ ...prev, [id]: null }));
    setProcessingFeed(prev => ({ ...prev, [id]: [] }));

    const es = new EventSource(
      `${API_BASE_URL}/accounts/${id}/fetch-emails-stream`,
      { withCredentials: true }
    );

    const appendFeed = (entry) =>
      setProcessingFeed(prev => ({ ...prev, [id]: [...(prev[id] ?? []), entry] }));

    es.onmessage = (e) => {
      let event;
      try {
        event = JSON.parse(e.data);
      } catch {
        return;
      }

      if (event.type === 'ping') return; // ignore heartbeats

      appendFeed(event);

      if (event.type === 'done') {
        es.close();
        setSyncing(prev => ({ ...prev, [id]: false }));
        setSyncResult(prev => ({
          ...prev,
          [id]: {
            type: 'success',
            message:
              `Synced ${event.count} new email${event.count !== 1 ? 's' : ''}` +
              (event.skipped > 0 ? `, skipped ${event.skipped} already synced.` : '.') +
              ' Go to Inbox to see them.',
          },
        }));
      }

      if (event.type === 'error') {
        es.close();
        setSyncing(prev => ({ ...prev, [id]: false }));
        setSyncResult(prev => ({
          ...prev,
          [id]: { type: 'error', message: `Sync failed: ${event.message}` },
        }));
      }
    };

    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) return;
      es.close();
      setSyncing(prev => ({ ...prev, [id]: false }));
      setSyncResult(prev => ({
        ...prev,
        [id]: { type: 'error', message: 'Connection lost during sync.' },
      }));
    };
  };

  return (
    <div className="accounts-page">
      <div className="accounts-page__head">
        <h1 className="accounts-page__title">Email Accounts</h1>
        <p className="accounts-page__subtitle">
          Connect and manage your Gmail accounts
        </p>
      </div>

      <div className="accounts-connect-row">
        <a href={`${API_BASE_URL}/auth/google`} className="connect-gmail-btn">
          <GmailIcon />
          Connect a Gmail account
        </a>
      </div>

      {accounts.length > 0 ? (
        <div className="accounts-list">
          {accounts.map(account => (
            <div key={account.id} className="account-card-wrapper">
              <div className="account-card">
                <div
                  className="account-card__avatar"
                  style={{ backgroundColor: avatarColor(account.email) }}
                >
                  {(account.email || '?')[0].toUpperCase()}
                </div>

                <div className="account-card__info">
                  <div className="account-card__email">{account.email}</div>
                  <div className="account-card__provider">{account.provider}</div>
                </div>

                <div className="account-card__actions">
                  <button
                    className="btn-sync"
                    onClick={() => handleSync(account.id)}
                    disabled={syncing[account.id]}
                    title="Fetch latest unread emails from this account"
                  >
                    <RefreshIcon />
                    {syncing[account.id] ? 'Syncing…' : 'Sync'}
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(account.id)}
                    title="Remove this account"
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>

              {syncResult[account.id] && (
                <div className={`sync-result sync-result--${syncResult[account.id].type}`}>
                  {syncResult[account.id].message}
                </div>
              )}

              {processingFeed[account.id]?.length > 0 && (
                <ProcessingFeed feed={processingFeed[account.id]} />
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="accounts-empty">
          No accounts connected yet. Connect a Gmail account above to get started.
        </p>
      )}
    </div>
  );
};

export default Accounts;
